import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatError } from "../lib/api";
import { Truck, MapPin, Loader2, Eye, EyeOff } from "lucide-react";
import { isPodAsleepError, startKeepAlive, waitForPod } from "../lib/wakeUp";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [wakingUp, setWakingUp] = useState(false);
  const [wakeElapsed, setWakeElapsed] = useState(0);

  // Keep the preview pod awake while the user is on the login screen.
  useEffect(() => {
    const stop = startKeepAlive();
    return stop;
  }, []);

  const attemptLogin = async () => {
    const u = await login(email.trim().toLowerCase(), password.trim());
    toast.success(`Welcome back, ${u.name}`);
    nav(u.role === "admin" ? "/admin" : "/app", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await attemptLogin();
    } catch (err) {
      if (isPodAsleepError(err)) {
        // Pod is asleep / Cloudflare 502. Poll until it wakes, then retry login once.
        setWakingUp(true);
        setWakeElapsed(0);
        const woke = await waitForPod({
          maxMs: 30000,
          pollMs: 3000,
          onTick: (ms) => setWakeElapsed(ms),
        });
        setWakingUp(false);
        if (!woke) {
          toast.error(
            "Server is taking longer than usual to start. Please try again in a minute.",
          );
          setBusy(false);
          return;
        }
        try {
          await attemptLogin();
        } catch (err2) {
          toast.error(formatError(err2));
        }
      } else {
        toast.error(formatError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const fill = (e, p) => {
    setEmail(e);
    setPassword(p);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[color:var(--bg-base)]" data-testid="login-page">
      <div
        className="hidden md:flex flex-col justify-between p-10 text-white relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(28,26,23,0.85), rgba(28,26,23,0.55)), url('https://images.pexels.com/photos/8649386/pexels-photo-8649386.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3">
          <img src="/kridam-logo.png" alt="GM Kridam Foods" className="h-14 w-auto drop-shadow-lg" />
          <span className="font-display text-xl font-bold tracking-tight">GM Kridam Foods</span>
        </div>
        <div>
          <p className="overline text-white/70">Distribution Management</p>
          <h1 className="font-display text-5xl font-bold mt-3 leading-none">
            Routes, stores & orders — <br />in every salesman's pocket.
          </h1>
          <p className="mt-5 text-white/80 max-w-md">
            Run your daily beats with mobile check-ins, retail/wholesale pricing and live
            order capture. Built for the field, designed for the owner.
          </p>
          <div className="mt-8 flex gap-6 text-sm text-white/80">
            <div className="flex items-center gap-2"><Truck className="h-4 w-4" /> Route planning</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> GPS check-in</div>
          </div>
        </div>
        <div className="text-xs text-white/50">© {new Date().getFullYear()} GM Kridam Foods</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex items-center gap-3">
            <img src="/kridam-logo.png" alt="GM Kridam Foods" className="h-12 w-auto" />
            <span className="font-display text-xl font-bold">GM Kridam Foods</span>
          </div>
          <p className="overline text-[color:var(--muted)]">Sign in</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-[color:var(--ink)]">
            Welcome back
          </h2>
          <p className="mt-2 text-[color:var(--muted)]">
            Admins manage the catalog & routes. Salesmen take orders on the road.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 mt-1.5"
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-12"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy || wakingUp}
              className="w-full h-12 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white btn-tap"
              data-testid="login-submit-button"
            >
              {wakingUp ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Waking up server… {Math.floor(wakeElapsed / 1000)}s
                </span>
              ) : busy ? (
                "Signing in…"
              ) : (
                "Sign in"
              )}
            </Button>
            {wakingUp && (
              <div
                className="text-xs text-center text-[color:var(--muted)] px-2"
                data-testid="waking-up-banner"
              >
                The preview server is starting up. This can take up to 30 seconds — please don&apos;t refresh. We&apos;ll log you in automatically.
              </div>
            )}
          </form>

          <div className="mt-8 p-4 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-2)]">
            <p className="overline mb-2">Demo accounts</p>
            <div className="flex flex-col gap-2 text-sm">
              <button
                type="button"
                onClick={() => fill("admin@distro.app", "admin123")}
                className="text-left hover:underline"
                data-testid="demo-admin-fill"
              >
                <b>Admin:</b> admin@distro.app / admin123
              </button>
              <button
                type="button"
                onClick={() => fill("salesman@distro.app", "sales123")}
                className="text-left hover:underline"
                data-testid="demo-salesman-fill"
              >
                <b>Salesman:</b> salesman@distro.app / sales123
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
